import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useLangGraphSend } from '@assistant-ui/react-langgraph';

type JoinSignal = { threadId: string } | null;

/**
 * Invisible coordination component that detects when a thread has an active run
 * and triggers the stream callback to join it.
 *
 * Rendered inside the AssistantRuntimeProvider tree so it has access to
 * useLangGraphSend(). The signal ref is set by the `load` callback when it
 * detects a busy thread.
 */
export function JoinActiveStream({
  signalRef,
}: {
  signalRef: MutableRefObject<JoinSignal>;
}) {
  const send = useLangGraphSend();
  const sentRef = useRef<string | null>(null);
  // State counter to force re-renders so the effect retries after async load settles
  const [, setTick] = useState(0);

  useEffect(() => {
    const signal = signalRef.current;
    if (!signal) return;
    // Avoid joining the same thread twice
    if (sentRef.current === signal.threadId) return;

    try {
      const promise = send([], {
        runConfig: { configurable: { _join: signal.threadId } },
      });
      // Mark as sent only after send() didn't throw synchronously
      sentRef.current = signal.threadId;
      signalRef.current = null;
      promise.catch((err) => {
        console.warn('[JoinActiveStream] send failed, will not retry:', err);
      });
    } catch {
      // send() threw synchronously (e.g., extras not ready yet).
      // Schedule a re-render to retry on the next tick.
      setTimeout(() => setTick((t) => t + 1), 200);
    }
  });

  return null;
}
